import React, { useEffect, useRef, useState } from "react";
import Login from "../Login/Login";
import "../PostForm.css";
import "./AktualnosciManager.css";
import "./PartnersManager.css";

const API_BASE = "https://ekselowe.pl/api";

// Formularz pojedynczego partnera (logo + link) - używany do dodawania i edycji.
const PartnerFieldsForm = ({ initial, submitLabel, isLoggedin, busy, onSubmit, onCancel }) => {
  const [name, setName] = useState(initial.name || "");
  const [link, setLink] = useState(initial.link || "");
  const [imageUrl, setImageUrl] = useState(initial.image || "");
  const [uploadedImage, setUploadedImage] = useState(initial.image || "");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedExtension = [".jpg", ".jpeg", ".png", ".svg", ".webp"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (allowedExtension.includes("." + ext)) {
      setSelectedFile(file);
      setValidationError(null);
    } else {
      setSelectedFile(null);
      setValidationError("Złe rozszerzenie. Użyj .jpg, .png, .svg albo .webp.");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setValidationError("Najpierw wybierz plik.");
      return;
    }
    setUploading(true);
    try {
      const imageData = new FormData();
      imageData.append("file", selectedFile);
      imageData.append("token", isLoggedin);
      const response = await fetch(`${API_BASE}/imageupload.php`, {
        method: "POST",
        body: imageData,
      });
      const responseData = await response.json();
      setUploadedImage("/public/uploads/" + responseData.image_link);
      setImageUrl("");
    } catch (err) {
      setValidationError("Nie udało się wrzucić pliku na serwer.");
    } finally {
      setUploading(false);
    }
  };

  const resetImage = () => {
    setUploadedImage("");
    setSelectedFile(null);
    setValidationError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const image = uploadedImage || imageUrl;
    if (!image || !link) return;
    onSubmit({
      name,
      link,
      image: imageUrl,
      file: uploadedImage,
    });
  };

  const previewImage = uploadedImage || imageUrl;

  return (
    <form className="am-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <input
          type="text"
          placeholder="Nazwa firmy (opcjonalnie, widoczna np. po najechaniu)"
          className="form-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="form-row">
        <input
          type="text"
          placeholder="Link do strony firmy (np. https://...)"
          required
          className="form-input"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
      </div>
      <div className="form-row">
        {!uploadedImage && (
          <input
            placeholder="URL logo (albo wrzuć plik obok)"
            className="form-input"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        )}
        {!uploadedImage && (
          <div className="col col-3">
            <input type="file" ref={fileInputRef} accept=".jpg,.jpeg,.png,.svg,.webp" onChange={handleFileChange} />
            <button className="btn btn-primary" type="button" onClick={handleUpload} disabled={uploading}>
              {validationError ? validationError : uploading ? "Wysyłanie…" : "Wrzuć na serwer"}
            </button>
          </div>
        )}
      </div>
      {previewImage && (
        <div className="am-image-preview-row">
          <img src={previewImage} alt="" className="pm-logo-preview" />
          <button className="btn" type="button" onClick={resetImage}>Zmień logo</button>
        </div>
      )}
      <div className="am-form-actions">
        <button type="submit" className="submit-button" disabled={busy}>
          {busy ? "Zapisywanie…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>Anuluj</button>
        )}
      </div>
    </form>
  );
};

const PartnersManager = ({ isLoggedin, setIsLoggedin }) => {
  const [partners, setPartners] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchPartners = () => {
    setLoading(true);
    fetch(`${API_BASE}/getpartners.php`, {
      headers: { "Content-Type": "application/json; charset=UTF-8" },
    })
      .then((res) => res.json())
      .then((json) => setPartners(json || {}))
      .catch(() => setPartners({}))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isLoggedin) fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedin]);

  if (!isLoggedin) {
    return <Login setToken={setIsLoggedin} />;
  }

  const submitToApi = async (url, fields) => {
    setBusy(true);
    try {
      const body = new URLSearchParams({
        name: fields.name || "",
        link: fields.link || "",
        image: fields.image || "",
        file: fields.file || "",
        token: isLoggedin,
        ...(fields.id !== undefined ? { id: fields.id } : {}),
      });
      await fetch(url, { method: "POST", body });
      fetchPartners();
      setShowAddForm(false);
      setEditingKey(null);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = (fields) => submitToApi(`${API_BASE}/addpartner.php`, fields);
  const handleEditSubmit = (key, fields) => submitToApi(`${API_BASE}/editpartner.php`, { ...fields, id: key });

  const handleDelete = async (key, name) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć logo "${name || "bez nazwy"}"?`)) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE}/deletepartner.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idd: key, token: isLoggedin }),
      });
      fetchPartners();
    } finally {
      setBusy(false);
    }
  };

  const keys = Object.keys(partners).reverse();

  return (
    <div className="am-wrapper">
      <h1 className="am-title">Zarządzaj Logami Partnerów</h1>
      <p className="am-subtitle">
        Tutaj dodajesz, edytujesz i usuwasz loga firm pokazywane w sekcji „Firmy, z którymi
        współpracujemy” na stronie głównej. Kliknięcie w logo na stronie przenosi na podany link.
      </p>

      {!showAddForm && (
        <button className="submit-button am-add-toggle" onClick={() => { setShowAddForm(true); setEditingKey(null); }}>
          + Dodaj nowe logo
        </button>
      )}

      {showAddForm && (
        <div className="am-card am-add-card">
          <h2>Nowe logo</h2>
          <PartnerFieldsForm
            initial={{ name: "", link: "", image: "" }}
            submitLabel="Dodaj logo"
            isLoggedin={isLoggedin}
            busy={busy}
            onSubmit={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {loading && <p>Wczytywanie…</p>}

      <div className="am-list">
        {!loading && keys.length === 0 && <p>Nie masz jeszcze żadnych dodanych log.</p>}
        {keys.map((key) => {
          const partner = partners[key];
          const isEditing = editingKey === key;
          return (
            <div className="am-card" key={key}>
              {isEditing ? (
                <>
                  <h2>Edytuj logo</h2>
                  <PartnerFieldsForm
                    initial={partner}
                    submitLabel="Zapisz zmiany"
                    isLoggedin={isLoggedin}
                    busy={busy}
                    onSubmit={(fields) => handleEditSubmit(key, fields)}
                    onCancel={() => setEditingKey(null)}
                  />
                </>
              ) : (
                <div className="am-row">
                  {partner.image && <img src={partner.image} alt={partner.name || ""} className="am-thumb pm-thumb" />}
                  <div className="am-row-text">
                    <h3>{partner.name || "(bez nazwy)"}</h3>
                    <p className="pm-link-text">{partner.link}</p>
                  </div>
                  <div className="am-row-actions">
                    <button className="btn" onClick={() => { setEditingKey(key); setShowAddForm(false); }}>Edytuj</button>
                    <button className="btn am-delete" onClick={() => handleDelete(key, partner.name)}>Usuń</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PartnersManager;
